import { createContext, useContext } from 'react'

import type { ApiClient, Member, Role, TripSummary } from './api'
import type { ZugriffWeg } from './sync'

/**
 * Woran die Reiseschicht gerade ist.
 *
 * - 'laedt': die Liste wird geholt. Beim ersten Start ohne Zwischenspeicher.
 * - 'auswahl': es ist zu entscheiden, welche Reise aufgeht - oder es gibt noch
 *   keine und es ist eine anzulegen.
 * - 'offen': eine Reise ist gewählt, die App läuft darin.
 * - 'fehler': die Liste liess sich nicht holen und es liegt auch keine
 *   gespeicherte vor. Ohne Reise gibt es nichts anzuzeigen.
 */
export type TripPhase = 'laedt' | 'auswahl' | 'offen' | 'fehler'

/** Alles, was die Oberfläche über die Reise und die eigene Rolle braucht. */
export interface TripValue {
  phase: TripPhase
  /** Die Reisen des Nutzers, älteste zuerst. */
  trips: TripSummary[]
  /** Die offene Reise. Bei einem Altbestand ein Platzhalter mit leerer ID. */
  aktiv: TripSummary | null
  /**
   * Wahr, solange der lokale Bestand noch keiner Reise auf dem Server
   * zugeordnet ist (siehe ALTBESTAND in db.ts). Dann läuft die App, aber es
   * wird nichts abgeglichen - niemand weiss, wohin.
   */
  istAltbestand: boolean
  rolle: Role
  darfSchreiben: boolean
  darfVerwalten: boolean
  /** Die Mitglieder der offenen Reise, oder null solange unbekannt. */
  mitglieder: Member[] | null
  /** Die Adresse des Besitzers: ältestes Mitglied mit der Rolle admin. */
  besitzer: string | null
  /** Die eigene Adresse, aus dem Token. Leer, wenn sie sich nicht ermitteln liess. */
  meineEmail: string
  /** Ein Hinweis, der zu erklären hat, warum die Auswahl gerade dasteht. */
  meldung: string | null
  /** Ein Fehler, der die Reiseliste betrifft. */
  fehler: string | null

  oeffne: (tripId: string) => void
  /**
   * Zurück zur Reiseliste, mit einem Satz, der sagt warum.
   *
   * Ausdrücklich ohne Löschen: der lokale Bestand der verlassenen Reise
   * bleibt liegen. Wer beim Abgleich erfährt, dass ihm die Reise entzogen
   * wurde, darf nicht auch noch sein Tagebuch verlieren.
   */
  zurListe: (meldung?: string) => void
  neuLaden: () => Promise<void>
  /**
   * Legt eine Reise an und öffnet sie.
   *
   * @param mitBeispiel legt zusätzlich die vorgeplante Beispielreise hinein -
   *   die sechzehn Balkanetappen samt Vignetten und Ersatzteilkiste. Ohne das
   *   ist die neue Reise leer, und das ist der Normalfall: eine Reise nach
   *   Portugal hat mit der Balkanroute nichts zu tun. Siehe data/beispiel.ts.
   */
  anlegen: (name: string, mitBeispiel?: boolean) => Promise<void>
  umbenennen: (name: string) => Promise<void>
  loeschen: () => Promise<void>
  mitgliederLaden: () => Promise<void>
  setzeMitglied: (email: string, rolle: Role) => Promise<void>
  entferneMitglied: (email: string) => Promise<void>
  /** Meldet, dass der Abgleich keinen Zugriff mehr hat. Führt zurück zur Liste. */
  zugriffVerloren: (grund: ZugriffWeg) => void
  /** Räumt beim Abmelden alles weg, was diese Schicht gemerkt hat. */
  vergissAlles: () => void
  api: ApiClient
}

export const TripContext = createContext<TripValue | null>(null)

export function useTrip(): TripValue {
  const value = useContext(TripContext)
  if (!value) throw new Error('useTrip ausserhalb von TripProvider benutzt')
  return value
}
